import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../translations';
import api from '../utils/api';
import { trackCompleteRegistration } from '../utils/facebookPixel';

const Register = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validate passwords match
    if (formData.password !== formData.confirmPassword) {
      setError(language === 'en' ? 'Passwords do not match' : 'كلمات المرور غير متطابقة');
      setLoading(false);
      return;
    }

    // Validate password length
    if (formData.password.length < 6) {
      setError(language === 'en' ? 'Password must be at least 6 characters' : 'كلمة المرور يجب أن تكون 6 أحرف على الأقل');
      setLoading(false);
      return;
    }

    try {
      const { confirmPassword, ...registerData } = formData;

      // Ensure phone is not the same as email and is properly formatted
      if (registerData.phone && registerData.phone === registerData.email) {
        registerData.phone = ''; // Clear phone if it matches email
      }

      // Only send phone if it has a value
      if (!registerData.phone || registerData.phone.trim() === '') {
        delete registerData.phone;
      } else {
        registerData.phone = registerData.phone.trim();
      }

      const res = await api.post('/auth/register', registerData);
      localStorage.setItem('token', res.data.token);
      localStorage.setItem('user', JSON.stringify(res.data.user));
      
      // Track Facebook Pixel CompleteRegistration event
      trackCompleteRegistration({
        content_name: 'User Registration',
        status: 'completed',
        value: 0,
        currency: 'EGP'
      });

      // Redirect based on role (customer goes to home, admin/staff to admin)
      if (res.data.user.role === 'admin' || res.data.user.role === 'staff') {
        navigate('/admin');
      } else {
        navigate('/');
      }
    } catch (error) {
      setError(error.response?.data?.error || (language === 'en' ? 'Registration failed. Please try again.' : 'فشل التسجيل. حاول مرة أخرى.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-blackC flex">
      {/* Left Side - Decorative Panel */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        {/* Background Gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-blackC via-dark to-charcoal" />

        {/* Animated Circles */}
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] rounded-full border border-primary/10 animate-pulse" style={{ animationDuration: '4s' }} />
          <div className="absolute top-1/3 left-1/3 w-[400px] h-[400px] rounded-full border border-primary/15 animate-pulse" style={{ animationDuration: '3s', animationDelay: '0.5s' }} />
          <div className="absolute top-[40%] left-[40%] w-[300px] h-[300px] rounded-full border border-primary/20 animate-pulse" style={{ animationDuration: '2.5s', animationDelay: '1s' }} />
          <div className="absolute top-[45%] left-[45%] w-[200px] h-[200px] rounded-full bg-primary/5 blur-3xl" />
        </div>

        {/* Diagonal Lines */}
        <svg className="absolute inset-0 w-full h-full opacity-10" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="diagonalLines" patternUnits="userSpaceOnUse" width="40" height="40" patternTransform="rotate(45)">
              <line x1="0" y1="0" x2="0" y2="40" stroke="#c9a962" strokeWidth="0.5" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#diagonalLines)" />
        </svg>

        {/* Content */}
        <div className={`relative z-10 flex flex-col justify-center p-16 xl:p-24 transition-all duration-1000 ${mounted ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
          {/* Logo */}
          <div className="mb-16">
            <span className="font-display text-5xl">
              <span className="text-primary">L</span>
              <span className="text-cream">imousine</span>
            </span>
            <div className="w-24 h-px bg-gradient-to-r from-primary to-transparent mt-4" />
          </div>

          {/* Quote */}
          <div className="space-y-8">
            <svg className="w-12 h-12 text-primary/40" fill="currentColor" viewBox="0 0 24 24">
              <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
            </svg>

            <blockquote className="font-display text-3xl xl:text-4xl text-cream leading-relaxed">
              {language === 'en'
                ? 'Join our exclusive community. Experience luxury travel at its finest.'
                : 'انضم إلى مجتمعنا الحصري. اختبر السفر الفاخر في أفضل حالاته.'}
            </blockquote>

            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                </svg>
              </div>
              <div>
                <p className="text-cream font-medium">{language === 'en' ? 'Premium Membership' : 'عضوية متميزة'}</p>
                <p className="text-gray-light text-sm">{language === 'en' ? 'Free to join' : 'انضم مجاناً'}</p>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-8 mt-16 pt-16 border-t border-gray-dark">
            {[
              { value: '50K+', label: language === 'en' ? 'Members' : 'عضو' },
              { value: '100+', label: language === 'en' ? 'Luxury Cars' : 'سيارة فاخرة' },
              { value: '24/7', label: language === 'en' ? 'Support' : 'دعم متواصل' }
            ].map((stat, idx) => (
              <div key={idx} className="text-center">
                <p className="font-display text-3xl text-primary">{stat.value}</p>
                <p className="text-gray-light text-sm mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Side - Registration Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-6 py-12 lg:px-12 relative">
        {/* Mobile Background Elements */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden lg:hidden">
          <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-primary/5 rounded-full blur-[80px]" />
          <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-primary/5 rounded-full blur-[80px]" />
        </div>

        <div className={`w-full max-w-md relative z-10 transition-all duration-1000 delay-300 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-12">
            <Link to="/" className="inline-block">
              <span className="font-display text-4xl">
                <span className="text-primary">L</span>
                <span className="text-cream">imousine</span>
              </span>
            </Link>
          </div>

          {/* Header */}
          <div className="mb-10">
            <p className="text-primary text-sm tracking-[0.3em] uppercase mb-3">
              {language === 'en' ? 'Join Us' : 'انضم إلينا'}
            </p>
            <h1 className="font-display text-4xl lg:text-5xl text-cream mb-4">
              {language === 'en' ? 'Create Account' : 'إنشاء حساب'}
            </h1>
            <p className="text-gray-light">
              {language === 'en'
                ? 'Sign up to book rides and manage your reservations'
                : 'سجل للاحتفاظ برحلاتك وإدارة حجوزاتك'}
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-8 p-4 bg-red-500/5 border border-red-500/20 rounded-lg flex items-start gap-3 animate-shake">
              <div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <p className="text-red-400 font-medium">{language === 'en' ? 'Registration Failed' : 'فشل التسجيل'}</p>
                <p className="text-red-400/70 text-sm mt-1">{error}</p>
              </div>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Name Field */}
            <div className="relative group">
              <label
                className={`absolute transition-all duration-300 pointer-events-none ${focusedField === 'name' || formData.name
                  ? 'text-xs text-primary top-2 left-4'
                  : 'text-gray-light top-1/2 -translate-y-1/2 left-14'
                  }`}
              >
                {t.fullName}
              </label>
              <div className={`absolute left-4 top-1/2 -translate-y-1/2 transition-colors duration-300 ${focusedField === 'name' ? 'text-primary' : 'text-[#4a4a4a]'}`}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                onFocus={() => setFocusedField('name')}
                onBlur={() => setFocusedField(null)}
                className="w-full bg-dark border border-gray-dark text-cream pl-14 pr-4 pt-6 pb-3 focus:outline-none focus:border-primary/50 focus:bg-charcoal transition-all duration-300 rounded-lg"
                required
                dir={language === 'ar' ? 'rtl' : 'ltr'}
              />
              <div className={`absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-primary to-[#d4af37] transition-all duration-500 rounded-full ${focusedField === 'name' ? 'w-full' : 'w-0'}`} />
            </div>

            {/* Email Field */}
            <div className="relative group">
              <label
                className={`absolute transition-all duration-300 pointer-events-none ${focusedField === 'email' || formData.email
                  ? 'text-xs text-primary top-2 left-4'
                  : 'text-gray-light top-1/2 -translate-y-1/2 left-14'
                  }`}
              >
                {t.email}
              </label>
              <div className={`absolute left-4 top-1/2 -translate-y-1/2 transition-colors duration-300 ${focusedField === 'email' ? 'text-primary' : 'text-[#4a4a4a]'}`}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                onFocus={() => setFocusedField('email')}
                onBlur={() => setFocusedField(null)}
                className="w-full bg-dark border border-gray-dark text-cream pl-14 pr-4 pt-6 pb-3 focus:outline-none focus:border-primary/50 focus:bg-charcoal transition-all duration-300 rounded-lg"
                required
                dir="ltr"
              />
              <div className={`absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-primary to-[#d4af37] transition-all duration-500 rounded-full ${focusedField === 'email' ? 'w-full' : 'w-0'}`} />
            </div>

            {/* Phone Field */}
            <div className="relative group">
              <label
                className={`absolute transition-all duration-300 pointer-events-none ${focusedField === 'phone' || formData.phone
                  ? 'text-xs text-primary top-2 left-4'
                  : 'text-gray-light top-1/2 -translate-y-1/2 left-14'
                  }`}
              >
                {t.phone}
              </label>
              <div className={`absolute left-4 top-1/2 -translate-y-1/2 transition-colors duration-300 ${focusedField === 'phone' ? 'text-primary' : 'text-[#4a4a4a]'}`}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
              </div>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                onFocus={() => setFocusedField('phone')}
                onBlur={() => setFocusedField(null)}
                autoComplete="tel"
                placeholder=""
                className="w-full bg-dark border border-gray-dark text-cream pl-14 pr-4 pt-6 pb-3 focus:outline-none focus:border-primary/50 focus:bg-charcoal transition-all duration-300 rounded-lg"
                dir="ltr"
              />
              <div className={`absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-primary to-[#d4af37] transition-all duration-500 rounded-full ${focusedField === 'phone' ? 'w-full' : 'w-0'}`} />
            </div>

            {/* Password Field */}
            <div className="relative group">
              <label
                className={`absolute transition-all duration-300 pointer-events-none ${focusedField === 'password' || formData.password
                  ? 'text-xs text-primary top-2 left-4'
                  : 'text-gray-light top-1/2 -translate-y-1/2 left-14'
                  }`}
              >
                {language === 'en' ? 'Password' : 'كلمة المرور'}
              </label>
              <div className={`absolute left-4 top-1/2 -translate-y-1/2 transition-colors duration-300 ${focusedField === 'password' ? 'text-primary' : 'text-[#4a4a4a]'}`}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={formData.password}
                onChange={handleChange}
                onFocus={() => setFocusedField('password')}
                onBlur={() => setFocusedField(null)}
                className="w-full bg-dark border border-gray-dark text-cream pl-14 pr-14 pt-6 pb-3 focus:outline-none focus:border-primary/50 focus:bg-charcoal transition-all duration-300 rounded-lg"
                required
                dir="ltr"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-[#4a4a4a] hover:text-primary transition-colors p-1"
              >
                {showPassword ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
              <div className={`absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-primary to-[#d4af37] transition-all duration-500 rounded-full ${focusedField === 'password' ? 'w-full' : 'w-0'}`} />
            </div>

            {/* Confirm Password Field */}
            <div className="relative group">
              <label
                className={`absolute transition-all duration-300 pointer-events-none ${focusedField === 'confirmPassword' || formData.confirmPassword
                  ? 'text-xs text-primary top-2 left-4'
                  : 'text-gray-light top-1/2 -translate-y-1/2 left-14'
                  }`}
              >
                {language === 'en' ? 'Confirm Password' : 'تأكيد كلمة المرور'}
              </label>
              <div className={`absolute left-4 top-1/2 -translate-y-1/2 transition-colors duration-300 ${focusedField === 'confirmPassword' ? 'text-primary' : 'text-[#4a4a4a]'}`}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                onFocus={() => setFocusedField('confirmPassword')}
                onBlur={() => setFocusedField(null)}
                className="w-full bg-dark border border-gray-dark text-cream pl-14 pr-14 pt-6 pb-3 focus:outline-none focus:border-primary/50 focus:bg-charcoal transition-all duration-300 rounded-lg"
                required
                dir="ltr"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-[#4a4a4a] hover:text-primary transition-colors p-1"
              >
                {showConfirmPassword ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
              <div className={`absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-primary to-[#d4af37] transition-all duration-500 rounded-full ${focusedField === 'confirmPassword' ? 'w-full' : 'w-0'}`} />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="relative w-full group overflow-hidden"
            >
              <div className="relative bg-gradient-to-r from-primary to-[#d4af37] text-blackC font-semibold py-4 px-6 flex items-center justify-center gap-3 rounded-lg transition-all duration-300 group-hover:shadow-lg group-hover:shadow-primary/20">
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-blackC/20 border-t-blackC rounded-full animate-spin" />
                    {language === 'en' ? 'Creating account...' : 'جاري إنشاء الحساب...'}
                  </>
                ) : (
                  <>
                    {language === 'en' ? 'Create Account' : 'إنشاء الحساب'}
                    <svg className="w-5 h-5 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </>
                )}
              </div>
              <div className="absolute inset-0 bg-white/20 translate-x-[-100%] skew-x-12 group-hover:translate-x-[100%] transition-transform duration-700" />
            </button>
          </form>

          {/* Divider */}
          <div className="flex items-center gap-4 my-10">
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-dark to-transparent" />
            <span className="text-[#4a4a4a] text-sm">{language === 'en' ? 'or' : 'أو'}</span>
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-dark to-transparent" />
          </div>

          {/* Login Link */}
          <div className="text-center">
            <p className="text-gray-light mb-4">
              {language === 'en' ? 'Already have an account?' : 'هل لديك حساب بالفعل؟'}
            </p>
            <Link
              to="/login"
              className="flex items-center justify-center gap-3 py-4 border border-gray-dark rounded-lg text-gray-silver hover:text-cream hover:border-primary/30 hover:bg-dark transition-all duration-300 group"
            >
              <svg className="w-5 h-5 transition-transform group-hover:-translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
              </svg>
              {language === 'en' ? 'Sign In' : 'تسجيل الدخول'}
            </Link>
          </div>

          {/* Back to Home */}
          <a
            href="/"
            className="flex items-center justify-center gap-3 py-4 mt-6 border border-gray-dark rounded-lg text-gray-silver hover:text-cream hover:border-primary/30 hover:bg-dark transition-all duration-300 group"
          >
            <svg className="w-5 h-5 transition-transform group-hover:-translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            {language === 'en' ? 'Back to Homepage' : 'العودة للصفحة الرئيسية'}
          </a>

          {/* Security Badge */}
          <div className="mt-10 flex items-center justify-center gap-3 text-[#4a4a4a]">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
            <span className="text-xs">
              {language === 'en' ? 'Secured with SSL encryption' : 'محمي بتشفير SSL'}
            </span>
          </div>

          {/* Footer */}
          <p className="text-center text-gray text-sm mt-8">
            © {new Date().getFullYear()} Limousine. {language === 'en' ? 'All rights reserved.' : 'جميع الحقوق محفوظة.'}
          </p>
        </div>
      </div>

      {/* Add shake animation */}
      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-4px); }
          20%, 40%, 60%, 80% { transform: translateX(4px); }
        }
        .animate-shake {
          animation: shake 0.5s ease-in-out;
        }
      `}</style>
    </div>
  );
};

export default Register;

